import React, { useState, useContext, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Button,
  Spinner,
  Alert,
  Switch,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from '@material-tailwind/react';
import { PlusIcon } from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import Select from 'react-select';
import { collection, doc, setDoc, addDoc, serverTimestamp, query, orderBy, where, getDocs, updateDoc } from 'firebase/firestore';
import db from '../../firebase';
import { AgGridReact } from 'ag-grid-react';
import { DataContext } from '../../context/DataContext';
import { UserAuth } from '../../context/AuthContext';
import AeristaBulkUpload from './AeristaBulkUpload';

const EMPTY_FORM = {
  reg: '',
  year: '',
  model: '',
  serial_no: '',
  broker: '',
  total_time: '',
  orig_price_usd: '',
  sale_price_usd: '',
  dt_listing: '',
  dtListingUnknown: false,
  dt_sold: '',
  manufacturer: '',
  dom: '',
  domUnknown: false,
  notes: '',
  auction: false,
};

const BROKER_OPTIONS = ['LMA', 'Aerista', 'AircraftBidder', 'CirrusNext', 'Other'].map((b) => ({
  value: b,
  label: b,
}));

const SOURCE_LABELS = {
  controller: 'Controller',
  tradeaplane: 'Trade-A-Plane',
  planecheck: 'PlaneCheck',
  aircraftcom: 'Aircraft.com',
};
const fmtSource = (s) =>
  SOURCE_LABELS[s] || (s ? s.charAt(0).toUpperCase() + s.slice(1) : 'PlanePortal');
const fmtSeenDate = (d) =>
  d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '?';

function SalesCompsPage() {
  const { user } = UserAuth();
  const dataContext = useContext(DataContext);
  const modelData = dataContext.getModelData();
  const manufacturerData = dataContext.getManufacturerData();

  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [stagedListings, setStagedListings] = useState([]); // PlanePortal matches: [{ days, source, firstSeen, lastSeen }]
  const [stagedDomLoading, setStagedDomLoading] = useState(false);
  const lookupTimer = useRef(null); // debounce handle for the reg lookup
  const lastLookedUpRef = useRef(''); // last reg actually queried, to skip duplicate reads
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [existingSales, setExistingSales] = useState([]);
  const [salesLoading, setSalesLoading] = useState(true);
  const [salesRefreshKey, setSalesRefreshKey] = useState(0);

  // Load existing sales comps
  useEffect(() => {
    const loadSales = async () => {
      try {
        setSalesLoading(true);
        const q = query(
          collection(db, 'lma_sales_comps'),
          orderBy('dt_sold', 'desc')
        );
        const snap = await getDocs(q);
        setExistingSales(snap.docs
          .map((d) => ({ ...d.data(), id: d.id }))
          .filter((d) => !d.deleted)
        );
      } catch (err) {
        console.error('Failed to load sales comps:', err);
      } finally {
        setSalesLoading(false);
      }
    };
    loadSales();
  }, [salesRefreshKey]);

  // Build manufacturer options from DataContext
  const manufacturerOptions = useMemo(() => {
    if (!Object.keys(manufacturerData).length) return [];
    return Object.keys(manufacturerData)
      .sort()
      .map((k) => ({ value: manufacturerData[k], label: k }));
  }, [manufacturerData]);

  // Build model options filtered by selected manufacturer
  const modelOptions = useMemo(() => {
    if (!Object.keys(modelData).length || !selectedManufacturer) return [];
    const options = [];
    for (const [key, value] of Object.entries(modelData)) {
      const manufacturersForModel = new Set(value.manufacturers);
      const manufacturerIds = new Set(selectedManufacturer.value?.ids || []);
      const hasIntersection = [...manufacturersForModel].some(id => manufacturerIds.has(id));
      if (hasIntersection) {
        options.push({ value, label: key });
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [modelData, selectedManufacturer]);

  const formatNumberInput = (val) => {
    if (!val) return '';
    const num = String(val).replace(/[^0-9]/g, '');
    return num ? parseInt(num, 10).toLocaleString('en-US') : '';
  };

  const handlePriceChange = (field, val) => {
    const raw = val.replace(/[^0-9]/g, '');
    updateField(field, raw);
  };

  const handleManufacturerChange = (option) => {
    setSelectedManufacturer(option);
    setSelectedModel(null);
  };

  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Find every PlanePortal listing matching this reg and stage each one's
  // days-on-market (with its listing window) as an optional suggestion. When a
  // reg was listed more than once, the user picks which window applies.
  const lookupStagedDom = useCallback(async (regValue) => {
    const regUp = (regValue || '').toUpperCase().trim();
    if (regUp.length < 4) {
      setStagedListings([]);
      return;
    }
    if (regUp === lastLookedUpRef.current) return; // already have results for this reg
    lastLookedUpRef.current = regUp;
    try {
      setStagedDomLoading(true);
      const toDate = (ts) => {
        if (!ts) return null;
        if (ts.toDate) return ts.toDate();
        if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
        return null;
      };
      const snap = await getDocs(
        query(collection(db, 'listing_aggregations_v1'), where('reg', '==', regUp))
      );
      const listings = [];
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        const firstSeen = toDate(data.first_seen);
        const lastSeen = toDate(data.last_seen);
        let days = null;
        if (typeof data.days_active === 'number') {
          days = data.days_active;
        } else if (firstSeen && lastSeen) {
          days = (lastSeen.getTime() - firstSeen.getTime()) / 86400000;
        }
        if (days == null) return;
        listings.push({
          days: Math.round(days),
          source: data.source || 'PlanePortal',
          firstSeen,
          lastSeen,
        });
      });
      // Most recently active listing first.
      listings.sort((a, b) => (b.lastSeen?.getTime() || 0) - (a.lastSeen?.getTime() || 0));
      setStagedListings(listings);
    } catch (err) {
      console.error('Staged DOM lookup failed', err);
      setStagedListings([]);
      lastLookedUpRef.current = ''; // allow a retry after a failed lookup
    } finally {
      setStagedDomLoading(false);
    }
  }, []);

  // Changing the reg invalidates any DOM picked for the previous reg, so clear
  // it (and the staged options) immediately, then debounce a fresh lookup so it
  // runs live as the user types (once the reg is long enough to match).
  const handleRegChange = useCallback((value) => {
    const v = (value || '').toUpperCase();
    setForm((prev) => ({ ...prev, reg: v, dom: '' }));
    setStagedListings([]);
    lastLookedUpRef.current = '';
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    if (v.trim().length >= 4) {
      lookupTimer.current = setTimeout(() => lookupStagedDom(v), 400);
    }
  }, [lookupStagedDom]);

  // Leaving the field forces the lookup immediately (covers a fast type-then-tab
  // before the debounce fires); the dedupe guard prevents a duplicate read.
  const handleRegBlur = useCallback((value) => {
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    lookupStagedDom(value);
  }, [lookupStagedDom]);

  // Group matches by source; a per-source total only sums listings from that
  // same source (so the same aircraft on two portals isn't double-counted).
  const stagedGroups = useMemo(() => {
    const map = new Map();
    for (const l of stagedListings) {
      const key = l.source || 'PlanePortal';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    }
    return Array.from(map.entries()).map(([source, items]) => ({
      source,
      items,
      total: items.reduce((sum, x) => sum + x.days, 0),
    }));
  }, [stagedListings]);

  // Generate deterministic doc ID for dedup
  const getDocId = (reg, dtSold) => {
    const cleanReg = (reg || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    const cleanDate = dtSold || '';
    return `${cleanReg}_${cleanDate}`;
  };

  const isFormValid =
    form.reg.trim() &&
    form.year &&
    selectedManufacturer &&
    selectedModel &&
    form.serial_no.trim() &&
    form.broker.trim() &&
    form.total_time &&
    form.orig_price_usd &&
    form.sale_price_usd &&
    (form.dt_listing || form.dtListingUnknown) &&
    form.dt_sold &&
    (form.dom || form.domUnknown);

  const hasChanges = useMemo(() => {
    if (!editingRecord) return true;
    const toDateStr = (val) => {
      if (!val) return '';
      const d = val.toDate ? val.toDate() : val instanceof Date ? val : new Date(val);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().split('T')[0];
    };
    const mfrIds = selectedManufacturer?.value?.ids || [];
    return (
      form.reg.toUpperCase().trim() !== (editingRecord.reg || '') ||
      form.year !== (editingRecord.year?.toString() || '') ||
      (selectedModel?.label || '') !== (editingRecord.model || '') ||
      (mfrIds[0] || form.manufacturer) !== (editingRecord.manufacturer || '') ||
      form.serial_no !== (editingRecord.serial_no || '') ||
      form.broker !== (editingRecord.broker || '') ||
      form.total_time !== (editingRecord.total_time?.toString() || '') ||
      form.orig_price_usd !== (editingRecord.orig_price_usd?.toString() || '') ||
      form.sale_price_usd !== (editingRecord.sale_price_usd?.toString() || '') ||
      form.dt_listing !== toDateStr(editingRecord.dt_listing) ||
      form.dtListingUnknown !== (editingRecord.dt_listing == null) ||
      form.dt_sold !== toDateStr(editingRecord.dt_sold) ||
      form.dom !== (editingRecord.dom?.toString() || '') ||
      form.domUnknown !== (editingRecord.dom == null) ||
      form.auction !== (editingRecord.auction || false) ||
      form.notes !== (editingRecord.notes || '')
    );
  }, [form, editingRecord, selectedManufacturer, selectedModel]);

  const handleSubmit = async () => {
    if (!isFormValid) return;

    try {
      setIsSaving(true);
      setError(null);

      const docId = editingRecord ? editingRecord.id : getDocId(form.reg, form.dt_sold);
      const modelLabel = selectedModel?.label || '';
      const manufacturerIds = selectedManufacturer?.value?.ids || [];

      const record = {
        reg: form.reg.toUpperCase().trim(),
        year: parseInt(form.year, 10),
        model: modelLabel,
        pp_model: modelData[modelLabel] ? modelLabel : 'UNKNOWN_MODEL',
        manufacturer: manufacturerIds[0] || form.manufacturer,
        serial_no: form.serial_no.trim() || null,
        broker: form.broker.trim() || null,
        total_time: form.total_time ? parseInt(form.total_time, 10) : null,
        orig_price_usd: form.orig_price_usd ? parseInt(form.orig_price_usd, 10) : null,
        sale_price_usd: parseInt(form.sale_price_usd, 10),
        dt_listing: form.dtListingUnknown ? null : (form.dt_listing ? new Date(form.dt_listing) : null),
        dt_sold: new Date(form.dt_sold),
        dom: form.domUnknown ? null : (form.dom ? parseInt(form.dom, 10) : null),
        notes: form.notes.trim() || null,
        auction: form.auction || false,
        deleted: false,
        enteredBy: user?.uid || null,
        enteredByName: user?.displayName || null,
        enteredAt: serverTimestamp(),
      };

      // Write to canonical collection
      const canonicalRef = doc(db, 'lma_sales_comps', docId);
      await setDoc(canonicalRef, record);

      // Audit trail — compute diff for edits
      const auditEntry = {
        action: editingRecord ? 'edited' : 'created',
        timestamp: serverTimestamp(),
        userId: user?.uid || null,
        userName: user?.displayName || null,
      };

      if (editingRecord) {
        const changes = {};
        const fields = ['reg', 'year', 'model', 'manufacturer', 'serial_no', 'broker',
          'total_time', 'orig_price_usd', 'sale_price_usd', 'dom', 'auction', 'notes'];
        for (const f of fields) {
          const oldVal = editingRecord[f] ?? null;
          const newVal = record[f] ?? null;
          if (String(oldVal) !== String(newVal)) {
            changes[f] = { from: oldVal, to: newVal };
          }
        }
        // Date fields — compare as strings
        const oldDtSold = editingRecord.dt_sold?.toDate
          ? editingRecord.dt_sold.toDate().toISOString().split('T')[0]
          : editingRecord.dt_sold instanceof Date ? editingRecord.dt_sold.toISOString().split('T')[0] : '';
        if (oldDtSold !== form.dt_sold) {
          changes.dt_sold = { from: oldDtSold, to: form.dt_sold };
        }
        const oldDtListing = editingRecord.dt_listing?.toDate
          ? editingRecord.dt_listing.toDate().toISOString().split('T')[0]
          : editingRecord.dt_listing instanceof Date ? editingRecord.dt_listing.toISOString().split('T')[0] : '';
        if (oldDtListing !== form.dt_listing) {
          changes.dt_listing = { from: oldDtListing, to: form.dt_listing };
        }
        auditEntry.changes = changes;
      }

      await addDoc(collection(canonicalRef, 'audit'), auditEntry);




      setSuccess(`Saved: ${record.reg} — ${modelLabel} (${form.dt_sold})`);
      setTimeout(() => setSuccess(null), 4000);

      if (editingRecord) {
        setForm(EMPTY_FORM);
        setSelectedManufacturer(null);
      } else {
        // Keep broker and manufacturer for quick re-entry
        setForm({ ...EMPTY_FORM, broker: form.broker });
      }
      setSelectedModel(null);
      setEditingRecord(null);
      setSalesRefreshKey((k) => k + 1);
      setShowAddDialog(false);
    } catch (err) {
      console.error('Failed to save sales comp:', err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (val) => {
    if (!val) return '';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
  };

  // Open dialog to edit an existing record
  const handleEdit = useCallback((record) => {
    const toDateStr = (val) => {
      if (!val) return '';
      const d = val.toDate ? val.toDate() : val instanceof Date ? val : new Date(val);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().split('T')[0];
    };

    setForm({
      reg: record.reg || '',
      year: record.year?.toString() || '',
      model: record.model || '',
      serial_no: record.serial_no || '',
      broker: record.broker || '',
      total_time: record.total_time?.toString() || '',
      orig_price_usd: record.orig_price_usd?.toString() || '',
      sale_price_usd: record.sale_price_usd?.toString() || '',
      dt_listing: toDateStr(record.dt_listing),
      dtListingUnknown: record.dt_listing == null,
      dt_sold: toDateStr(record.dt_sold),
      manufacturer: record.manufacturer || '',
      dom: record.dom?.toString() || '',
      domUnknown: record.dom == null,
      notes: record.notes || '',
      auction: record.auction || false,
    });

    // Find matching manufacturer option
    const mfrId = record.manufacturer;
    const mfrOption = manufacturerOptions.find((o) =>
      o.value?.ids?.includes(mfrId)
    ) || null;
    setSelectedManufacturer(mfrOption);

    // Find matching model option
    setSelectedModel(record.model ? { value: modelData[record.model], label: record.model } : null);

    setEditingRecord(record);
    setShowAddDialog(true);
    lookupStagedDom(record.reg);

    // Load audit log
    setAuditLoading(true);
    setAuditLog([]);
    getDocs(query(
      collection(db, 'lma_sales_comps', record.id, 'audit'),
      orderBy('timestamp', 'desc')
    )).then((snap) => {
      setAuditLog(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }).catch((err) => {
      console.error('Failed to load audit:', err);
    }).finally(() => {
      setAuditLoading(false);
    });
  }, [manufacturerOptions, modelData, lookupStagedDom]);

  // Format Firestore timestamp or Date for display
  const formatDate = (val) => {
    if (!val) return '';
    const d = val.toDate ? val.toDate() : val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  // Soft delete with audit trail
  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Delete this sales record?')) return;
    try {
      const ref = doc(db, 'lma_sales_comps', id);
      await updateDoc(ref, { deleted: true, deletedAt: serverTimestamp() });
      await addDoc(collection(ref, 'audit'), {
        action: 'deleted',
        timestamp: serverTimestamp(),
        userId: user?.uid || null,
        userName: user?.displayName || null,
      });
      setExistingSales((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error('Failed to delete:', err);
      setError('Failed to delete: ' + err.message);
    }
  }, [user]);

  // AG Grid column defs for existing sales
  const salesColDefs = useMemo(() => {
    const dateComparator = (a, b) => {
      const toMs = (v) => v?.toDate ? v.toDate().getTime() : v?._seconds ? v._seconds * 1000 : v instanceof Date ? v.getTime() : new Date(v).getTime() || 0;
      return toMs(a) - toMs(b);
    };
    const dateFilterValueGetter = (p, field) => {
      const v = p.data[field];
      if (!v) return null;
      if (v.toDate) return v.toDate();
      if (v._seconds) return new Date(v._seconds * 1000);
      if (v instanceof Date) return v;
      return new Date(v);
    };

    return [
      { field: 'reg', headerName: 'Reg', width: 100, pinned: 'left' },
      { field: 'year', headerName: 'Year', width: 80 },
      { field: 'manufacturer', headerName: 'Mfr', width: 120,
        valueFormatter: (p) => p.value ? dataContext.getManufacturerNameFromId(p.value) : '',
      },
      { field: 'model', headerName: 'Model', width: 140 },
      { field: 'sale_price_usd', headerName: 'Sale Price', width: 120,
        valueFormatter: (p) => p.value ? formatCurrency(p.value) : '',
      },
      { field: 'orig_price_usd', headerName: 'List Price', width: 120,
        valueFormatter: (p) => p.value ? formatCurrency(p.value) : '',
      },
      { field: 'total_time', headerName: 'TT', width: 80 },
      { field: 'serial_no', headerName: 'Serial', width: 100 },
      { field: 'broker', headerName: 'Broker', width: 120 },
      { field: 'dom', headerName: 'DOM', width: 70 },
      { field: 'auction', headerName: 'Auction', width: 80,
        valueFormatter: (p) => p.value ? 'Yes' : 'No',
        cellStyle: (p) => p.value ? { color: '#b45309', fontWeight: 600 } : {},
      },
      { field: 'dt_sold', headerName: 'Date Sold', width: 120,
        valueFormatter: (p) => formatDate(p.value),
        filter: 'agDateColumnFilter',
        filterValueGetter: (p) => dateFilterValueGetter(p, 'dt_sold'),
        comparator: dateComparator,
      },
      { field: 'dt_listing', headerName: 'Date Listed', width: 120,
        valueFormatter: (p) => formatDate(p.value),
        filter: 'agDateColumnFilter',
        filterValueGetter: (p) => dateFilterValueGetter(p, 'dt_listing'),
        comparator: dateComparator,
      },
      { field: 'notes', headerName: 'Notes', width: 150 },
      { field: 'enteredByName', headerName: 'Entered By', width: 110 },
      {
        headerName: '',
        width: 110,
        sortable: false,
        filter: false,
        cellRenderer: (params) => (
          <div className="flex gap-2">
            <button
              onClick={() => handleEdit(params.data)}
              className="text-blue-500 hover:text-blue-700 text-xs"
            >
              Edit
            </button>
            <button
              onClick={() => handleDelete(params.data.id)}
              className="text-red-400 hover:text-red-600 text-xs"
            >
              Delete
            </button>
          </div>
        ),
      },
    ];
  }, [handleDelete, handleEdit, dataContext]);


  // Chart data: monthly notional sales by broker
  const chartData = useMemo(() => {
    if (!existingSales.length) return [];
    const monthMap = {};
    const brokerSet = new Set();

    existingSales.forEach((s) => {
      if (!s.dt_sold || !s.sale_price_usd) return;
      const ts = s.dt_sold?.toDate ? s.dt_sold.toDate() : s.dt_sold?._seconds ? new Date(s.dt_sold._seconds * 1000) : new Date(s.dt_sold);
      if (isNaN(ts.getTime())) return;
      const monthKey = `${ts.getFullYear()}-${String(ts.getMonth() + 1).padStart(2, '0')}`;
      const broker = (s.broker || 'Unknown').trim();
      brokerSet.add(broker);

      if (!monthMap[monthKey]) monthMap[monthKey] = {};
      monthMap[monthKey][broker] = (monthMap[monthKey][broker] || 0) + s.sale_price_usd;
    });

    const sortedMonths = Object.keys(monthMap).sort();
    return {
      data: sortedMonths.map((m) => ({ month: m, ...monthMap[m] })),
      brokers: [...brokerSet].sort(),
    };
  }, [existingSales]);

  const BROKER_COLORS = ['#2563eb', '#d97706', '#059669', '#7c3aed', '#dc2626', '#0891b2', '#db2777', '#65a30d'];

  const handleCloseDialog = () => {
    setShowAddDialog(false);
    setEditingRecord(null);
    setAuditLog([]);
    setForm(EMPTY_FORM);
    setSelectedManufacturer(null);
    setSelectedModel(null);
    setStagedListings([]);
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    lastLookedUpRef.current = '';
  };

  const inputClass = "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500";

  return (
    <div className="p-6 h-[calc(100vh-100px)] flex flex-col">
      {error && (
        <Alert color="red" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert color="green" className="mb-4">
          {success}
        </Alert>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Typography variant="h5" color="blue-gray">
          Sales Comps ({existingSales.length})
        </Typography>
        <div className="flex items-center gap-2">
          <Button
            variant="text"
            size="sm"
            onClick={() => setSalesRefreshKey((k) => k + 1)}
            disabled={salesLoading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="sm"
            onClick={() => setShowBulkDialog(true)}
          >
            Bulk Upload (Aerista)
          </Button>
          <Button
            size="sm"
            className="flex items-center gap-1"
            onClick={() => setShowAddDialog(true)}
          >
            <PlusIcon className="h-4 w-4" />
            Record Sale
          </Button>
        </div>
      </div>

      <AeristaBulkUpload
        open={showBulkDialog}
        onClose={() => setShowBulkDialog(false)}
        user={user}
        onUploaded={() => setSalesRefreshKey((k) => k + 1)}
      />

      {/* Chart */}
      {chartData.data?.length > 0 && (
        <Card className="mb-4">
          <CardBody>
            <Typography variant="small" className="font-semibold text-gray-700 mb-2">
              Monthly Sales by Broker
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData.data}>
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`} />
                <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {chartData.brokers.map((broker, i) => (
                  <Bar key={broker} dataKey={broker} fill={BROKER_COLORS[i % BROKER_COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>
      )}

      {/* Grid */}
      <div className="flex-1 min-h-0">
          {salesLoading ? (
            <div className="flex items-center justify-center h-full">
              <Spinner className="h-6 w-6" />
            </div>
          ) : (
            <div className="ag-theme-quartz h-full">
              <AgGridReact
                rowData={existingSales}
                columnDefs={salesColDefs}
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  resizable: true,
                  suppressMovable: true,
                }}
                pagination={true}
                paginationPageSize={50}
                getRowId={(params) => params.data.id}
                domLayout="normal"
              />
            </div>
          )}
      </div>

      {/* Add Sale Dialog */}
      <Dialog open={showAddDialog} handler={handleCloseDialog} size="lg">
        <DialogHeader>{editingRecord ? 'Edit Sale' : 'Record Sale'}</DialogHeader>
        <DialogBody className="space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">Registration *</label>
              <input
                type="text"
                value={form.reg}
                onChange={(e) => handleRegChange(e.target.value)}
                onBlur={(e) => handleRegBlur(e.target.value)}
                placeholder="N123AB"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">Year *</label>
              <input
                type="number"
                value={form.year}
                onChange={(e) => updateField('year', e.target.value)}
                placeholder="2020"
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">Manufacturer *</label>
              <Select
                options={manufacturerOptions}
                value={selectedManufacturer}
                onChange={handleManufacturerChange}
                placeholder="Select manufacturer..."
                isClearable
                styles={{ control: (base) => ({ ...base, minHeight: '38px', fontSize: '14px' }) }}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">Model *</label>
              <Select
                options={modelOptions}
                value={selectedModel}
                onChange={setSelectedModel}
                placeholder={selectedManufacturer ? "Select model..." : "Select manufacturer first"}
                isDisabled={!selectedManufacturer}
                isClearable
                styles={{ control: (base) => ({ ...base, minHeight: '38px', fontSize: '14px' }) }}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">Serial No. *</label>
              <input
                type="text"
                value={form.serial_no}
                onChange={(e) => updateField('serial_no', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">Broker *</label>
              <Select
                options={BROKER_OPTIONS}
                value={BROKER_OPTIONS.find((o) => o.value === form.broker) || null}
                onChange={(opt) => updateField('broker', opt ? opt.value : '')}
                placeholder="Select broker..."
                isClearable
                styles={{ control: (base) => ({ ...base, minHeight: '38px', fontSize: '14px' }) }}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">Total Time (hrs) *</label>
              <input
                type="number"
                value={form.total_time}
                onChange={(e) => updateField('total_time', e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">Listing Price (USD) *</label>
              <input
                type="text"
                value={formatNumberInput(form.orig_price_usd)}
                onChange={(e) => handlePriceChange('orig_price_usd', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">Sale Price (USD) *</label>
              <input
                type="text"
                value={formatNumberInput(form.sale_price_usd)}
                onChange={(e) => handlePriceChange('sale_price_usd', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-gray-800">DOM (days) *</label>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.domUnknown}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setForm((prev) => ({ ...prev, domUnknown: checked, dom: checked ? '' : prev.dom }));
                      }}
                    />
                    Not known
                  </label>
                  {!form.domUnknown && form.dom !== '' && (
                    <button
                      type="button"
                      onClick={() => updateField('dom', '')}
                      className="text-[11px] font-semibold px-2 py-0.5 rounded border border-red-300 bg-red-50 text-red-600 hover:bg-red-100 hover:border-red-400 transition-colors"
                    >
                      Clear DOM
                    </button>
                  )}
                </div>
              </div>
              <input
                type="number"
                value={form.domUnknown ? '' : form.dom}
                disabled={form.domUnknown}
                onChange={(e) => updateField('dom', e.target.value)}
                className={`${inputClass}${form.domUnknown ? ' bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
              />
              {form.domUnknown ? (
                <p className="text-[11px] font-medium text-amber-700 mt-1">
                  Saved as blank (null) — excluded from DOM calculations.
                </p>
              ) : (
                <>
                  {stagedDomLoading && (
                    <p className="text-[11px] font-medium text-gray-600 mt-1">Checking PlanePortal…</p>
                  )}
                  {!stagedDomLoading && stagedListings.length > 0 && (
                    <div className="mt-1 space-y-1">
                      <select
                        value=""
                        onChange={(e) => {
                          if (e.target.value !== '') updateField('dom', e.target.value);
                        }}
                        className={`${inputClass} text-[12px] border-amber-400 bg-amber-50 text-amber-900`}
                      >
                        <option value="">Pick a PlanePortal DOM…</option>
                        {stagedGroups.map((g) => (
                          <optgroup key={g.source} label={fmtSource(g.source)}>
                            {g.items.map((l, i) => (
                              <option key={i} value={l.days}>
                                {fmtSeenDate(l.firstSeen)} – {fmtSeenDate(l.lastSeen)} · {l.days}d
                              </option>
                            ))}
                            {g.items.length > 1 && (
                              <option value={g.total}>
                                Total ({g.items.length} listings) · {g.total}d
                              </option>
                            )}
                          </optgroup>
                        ))}
                      </select>
                      <p className="text-[11px] text-gray-600">
                        Only use if the actual DOM isn't available to you.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-gray-800">Date Listed *</label>
                <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.dtListingUnknown}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm((prev) => ({ ...prev, dtListingUnknown: checked, dt_listing: checked ? '' : prev.dt_listing }));
                    }}
                  />
                  Not known
                </label>
              </div>
              <input
                type="date"
                value={form.dtListingUnknown ? '' : form.dt_listing}
                disabled={form.dtListingUnknown}
                onChange={(e) => updateField('dt_listing', e.target.value)}
                className={`${inputClass}${form.dtListingUnknown ? ' bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
              />
              {form.dtListingUnknown && (
                <p className="text-[11px] font-medium text-amber-700 mt-1">
                  Saved as blank (null) — excluded from listing-date calculations.
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">Date Sold *</label>
              <input
                type="date"
                value={form.dt_sold}
                onChange={(e) => updateField('dt_sold', e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="block text-xs font-semibold text-gray-800">Auction</label>
            <Switch
              checked={form.auction}
              onChange={() => updateField('auction', !form.auction)}
              color="amber"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-800 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              rows={2}
              className={`${inputClass} resize-y`}
            />
          </div>

          {editingRecord && (
            <div className="border-t border-gray-200 pt-3 mt-2">
              <Typography variant="small" className="font-semibold text-gray-600 mb-2">
                Audit Log
              </Typography>
              {auditLoading ? (
                <div className="flex justify-center py-2"><Spinner className="h-4 w-4" /></div>
              ) : auditLog.length === 0 ? (
                <Typography variant="small" className="text-gray-400">No audit entries</Typography>
              ) : (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {auditLog.map((entry) => {
                    const ts = entry.timestamp?.toDate
                      ? entry.timestamp.toDate()
                      : entry.timestamp?._seconds
                        ? new Date(entry.timestamp._seconds * 1000)
                        : null;
                    return (
                      <div key={entry.id} className="text-xs text-gray-500 py-1.5 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            entry.action === 'created' ? 'bg-green-50 text-green-700' :
                            entry.action === 'edited' ? 'bg-blue-50 text-blue-700' :
                            entry.action === 'deleted' ? 'bg-red-50 text-red-700' :
                            'bg-gray-50 text-gray-600'
                          }`}>
                            {entry.action}
                          </span>
                          <span>{entry.userName || entry.userId || 'Unknown'}</span>
                          <span className="text-gray-400">{ts ? ts.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                          {entry.source && <span className="text-gray-400 truncate">{entry.source}</span>}
                        </div>
                        {entry.changes && Object.keys(entry.changes).length > 0 && (
                          <div className="mt-1 ml-6 space-y-0.5">
                            {Object.entries(entry.changes)
                              .filter(([, change]) => change && typeof change === 'object' && 'from' in change && 'to' in change)
                              .filter(([, change]) => String(change.from ?? '') !== String(change.to ?? ''))
                              .map(([field, change]) => (
                                <div key={field} className="text-[11px]">
                                  <span className="font-medium text-gray-600">{field}:</span>{' '}
                                  <span className="text-red-500 line-through">{change.from != null ? String(change.from) : '(empty)'}</span>{' '}
                                  <span className="text-green-600">{change.to != null ? String(change.to) : '(empty)'}</span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button variant="text" onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || isSaving || !hasChanges}
          >
            {isSaving ? <Spinner className="h-4 w-4" /> : editingRecord ? 'Update' : 'Save Sale'}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

export default SalesCompsPage;
